import { useEffect } from 'react';
import { fromEvent, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

interface Script {
  url: string;
  hasLoaded: boolean;
  event: Observable<Event>;
}

interface ScriptLoaderProps {
  scriptUrl: string;
  onLoaded: () => void;
}

/**
 * Module to load scripts only once
 */
const cachedScript: Script[] = [];
export function ScriptLoader({ scriptUrl, onLoaded }: ScriptLoaderProps) {
  // Reload script when script url changes
  useEffect(loadScript, [scriptUrl]);

  // Load script
  function loadScript() {
    // If script has not started to load yet
    if (!cachedScript.some((s) => s.url === scriptUrl)) {
      // Create script tag
      const scriptElement = document.createElement('script');
      scriptElement.src = scriptUrl;
      // Add script to cached script list
      cachedScript.push({
        // Get notified when script is loaded. Accept only one event before completing
        event: fromEvent(scriptElement, 'load').pipe(take(1)),
        url: scriptUrl,
        hasLoaded: false,
      });

      // Add script tag to body
      document.body.appendChild(scriptElement);
    }

    const script = cachedScript.find((s) => s.url === scriptUrl);

    // If script has already finished loaded, simply execute
    // routine when script has loaded
    if (script.hasLoaded) {
      onScriptLoad();
    } else {
      // Otherwise, subscribe to event and when it completed,
      // execute routine
      script.event.subscribe({
        complete: onScriptLoad,
      });
    }
  }

  // Routine to execute when script has been loaded
  function onScriptLoad() {
    // Update script state to loaded
    const script = cachedScript.find((s) => s.url === scriptUrl);
    script.hasLoaded = true;
    // Notify
    onLoaded();
  }

  return null;
}
